import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useQuery } from "@tanstack/react-query";
import {
  fetchTransacoesFinanceirasFiltradas,
  type FinanceiroFiltroParams,
} from "@/hooks/useFinanceiro";

type ProximosVencimentosProps = {
  /** Quando informado, restringe aos recebíveis do vendedor */
  vendedorId?: string | null;
};

export function ProximosVencimentos({ vendedorId }: ProximosVencimentosProps) {
  const filtros = useMemo<FinanceiroFiltroParams | null>(
    () => (vendedorId ? { vendedorId } : null),
    [vendedorId]
  );

  const { data: transacoes = [] } = useQuery({
    queryKey: ["transacoes_financeiras", "proximos-vencimentos", vendedorId ?? "all"],
    queryFn: () =>
      fetchTransacoesFinanceirasFiltradas(filtros, { tipo: "entrada" }),
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const proximosVencimentos = useMemo(() => {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    const proximos30Dias = new Date(hoje.getTime() + 30 * 24 * 60 * 60 * 1000);

    return transacoes
      .filter((transacao) => {
        if (!transacao.data_vencimento) return false;
        const dataVencimento = new Date(`${transacao.data_vencimento}T00:00:00`);
        return (
          transacao.tipo === "entrada" &&
          transacao.status === "pendente" &&
          dataVencimento >= hoje &&
          dataVencimento <= proximos30Dias
        );
      })
      .sort(
        (a, b) =>
          new Date(`${a.data_vencimento}T00:00:00`).getTime() -
          new Date(`${b.data_vencimento}T00:00:00`).getTime()
      )
      .slice(0, 5);
  }, [transacoes]);

  const calcularDiasAteVencimento = (dataVencimento: string) => {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    const vencimento = new Date(`${dataVencimento}T00:00:00`);
    const diffTime = vencimento.getTime() - hoje.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const formatarData = (data: string) =>
    new Date(`${data}T00:00:00`).toLocaleDateString("pt-BR");

  const formatarValor = (valor: number) =>
    `R$ ${valor.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`;

  if (proximosVencimentos.length === 0) {
    return (
      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle className="text-gray-900">Próximos Vencimentos</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center text-gray-500 py-8">
            Nenhum vencimento nos próximos 30 dias
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-0 shadow-md">
      <CardHeader>
        <CardTitle className="text-gray-900">Próximos Vencimentos</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {proximosVencimentos.map((transacao) => {
            const diasAteVencimento = calcularDiasAteVencimento(transacao.data_vencimento);
            const clienteNome =
              transacao.cliente?.nome || `Cliente ID: ${transacao.cliente_id}`;

            return (
              <div
                key={transacao.id}
                className="flex justify-between items-center p-3 bg-yellow-50 rounded-lg"
              >
                <div>
                  <div className="font-medium text-gray-900">
                    {formatarData(transacao.data_vencimento)} - {clienteNome}
                  </div>
                  <div className="text-sm text-gray-600">
                    Vence em {diasAteVencimento} dia
                    {diasAteVencimento !== 1 ? "s" : ""}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-bold text-gray-900">
                    {formatarValor(transacao.valor)}
                  </div>
                  <Badge className="bg-yellow-100 text-yellow-800 text-xs">
                    Pendente
                  </Badge>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
